'use client';

import React, { useEffect, useRef } from 'react';

const businessAreas = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <rect x="4" y="8" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M10 8V6C10 4.9 10.9 4 12 4H16C17.1 4 18 4.9 18 6V8" stroke="currentColor" strokeWidth="2"/>
        <path d="M14 13V19M11 16H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Pharmaceutical Portfolio',
    description: 'Products across multiple therapeutic segments.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M14 3L17.5 10.5L26 11.5L20 17.5L21.5 26L14 22L6.5 26L8 17.5L2 11.5L10.5 10.5L14 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    title: 'Quality Focus',
    description: 'A commitment to quality-oriented healthcare solutions.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M14 9V14L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M4 14H2M26 14H24M14 4V2M14 26V24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Healthcare Access',
    description: 'Focused on making relevant healthcare products accessible through an organized pharmaceutical business approach.',
  },
];

export default function BusinessSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="business" ref={sectionRef} className="py-20 lg:py-28 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: Content */}
          <div className="lg:col-span-5 space-y-6">
            <div className="section-reveal stagger-1">
              <span className="section-label">Business</span>
              <h2 className="font-display text-foreground mt-3 leading-tight" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>
                Business & Healthcare Solutions
              </h2>
            </div>
            <p className="section-reveal stagger-2 text-muted-foreground leading-relaxed">
              EWIN Lifescience operates with a focus on pharmaceutical healthcare products across diverse therapeutic segments. Our portfolio is designed to serve varied healthcare requirements through a range of pharmaceutical formulations.
            </p>
            <div className="section-reveal stagger-3">
              <a href="#contact" className="pharma-btn-primary">
                Get in Touch
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M3 9H15M10 4l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Cards */}
          <div className="lg:col-span-7 grid sm:grid-cols-1 gap-5">
            {businessAreas?.map((area, i) => (
              <div key={area?.title} className={`value-card flex items-start gap-5 section-reveal stagger-${i + 2}`}>
                <div className="w-12 h-12 bg-blue-50 text-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  {area?.icon}
                </div>
                <div>
                  <h3 className="font-display text-foreground text-lg mb-1.5">{area?.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{area?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}